/**
 * The site footer.
 *
 * Also the compliance surface: Apple and Google both require a reachable
 * privacy policy and a support route from the product's marketing site, and the
 * account-deletion link is a Google Play requirement in its own right (it must
 * be reachable without installing the app). Removing any of those links is a
 * store-review problem, not a design choice — see docs/legal.html.
 */
import { Link } from 'react-router-dom';

import { useI18n } from '../i18n';
import { COMPANY, PRODUCT } from '../lib/constants';
import { Logo } from './Logo';
import styles from './Footer.module.css';

export function Footer() {
  const { t, localePath } = useI18n();
  const year = new Date().getFullYear();

  return (
    <footer className={styles.footer}>
      <div className="container">
        <div className={styles.grid}>
          <div className={styles.brandCol}>
            <Link to={localePath('/')} className={styles.brand}>
              <Logo size={24} />
              <span>TwoCoins</span>
            </Link>
            <p className={styles.tagline}>{t.footer.tagline}</p>
          </div>

          <div>
            <h2 className={styles.colTitle}>{t.footer.columns.product}</h2>
            <ul className={styles.list}>
              <li>
                <a href={`${localePath('/')}#problem`.replace('//', '/')} className={styles.link}>
                  {t.footer.links.gap}
                </a>
              </li>
              <li>
                <a href={`${localePath('/')}#features`.replace('//', '/')} className={styles.link}>
                  {t.footer.links.features}
                </a>
              </li>
              <li>
                <a href={`${localePath('/')}#pricing`.replace('//', '/')} className={styles.link}>
                  {t.footer.links.pricing}
                </a>
              </li>
              <li>
                <a href={`${localePath('/')}#faq`.replace('//', '/')} className={styles.link}>
                  {t.footer.links.faq}
                </a>
              </li>
              <li>
                <Link to={localePath('/guides')} className={styles.link}>
                  {t.footer.links.guides}
                </Link>
              </li>
              <li>
                {/* The app itself. A link now, because there is one to give. */}
                <Link to="/app" className={styles.link}>
                  {t.footer.links.openApp}
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h2 className={styles.colTitle}>{t.footer.columns.legal}</h2>
            <ul className={styles.list}>
              <li>
                <Link to={localePath('/privacy')} className={styles.link}>
                  {t.footer.links.privacy}
                </Link>
              </li>
              <li>
                <Link to={localePath('/terms')} className={styles.link}>
                  {t.footer.links.terms}
                </Link>
              </li>
              <li>
                <Link to={localePath('/cookies')} className={styles.link}>
                  {t.footer.links.cookies}
                </Link>
              </li>
              <li>
                <Link to={localePath('/delete-account')} className={styles.link}>
                  {t.footer.links.deleteAccount}
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h2 className={styles.colTitle}>{t.footer.columns.company}</h2>
            <ul className={styles.list}>
              <li>
                <Link to={localePath('/support')} className={styles.link}>
                  {t.footer.links.support}
                </Link>
              </li>
              <li>
                <a href={`mailto:${COMPANY.email}`} className={styles.link}>
                  {t.footer.links.contact}
                </a>
              </li>
              <li>
                <a
                  href={PRODUCT.instagramUrl}
                  className={styles.link}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t.footer.links.instagram}
                </a>
              </li>
              <li>
                <a
                  href={COMPANY.url}
                  className={styles.link}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t.footer.links.buildpassing}
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className={styles.bottom}>
          <span>
            © {year} {COMPANY.legalName}. {t.footer.rights}
          </span>
          <span className={styles.builtBy}>
            {t.footer.builtBy}{' '}
            <a href={COMPANY.url} target="_blank" rel="noopener noreferrer">
              BuildPassing
            </a>
          </span>
        </div>
      </div>
    </footer>
  );
}
